import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { useAppStore } from "../../store/app";
import { useT } from "../../lib/i18n/useT";
import { readBestCvText, readProfileJson, saveMasterCv, writeProfileJson, type Profile } from "../../lib/api/profile";
import { readAllWorkflows, terminateWorkflow, type Workflow } from "../../lib/api/workflows";
import { readAllVacancies, type Vacancy } from "../../lib/api/vacancies";
import { openExternal } from "../../lib/browser/download";
import { extractProfileFromCV } from "../../lib/ai/profile-extraction";
import { fetchGraphMe } from "../../lib/graph/me";

function categorizeSkill(skillName: string): "tech" | "domain" | "other" {
  const name = skillName.toLowerCase();
  const techKeywords = [
    "java", "react", "next", "vue", "angular", "node", "python", "javascript", "typescript", 
    "docker", "kubernetes", "aws", "azure", "gcp", "sql", "postgres", "mysql", "mongodb", 
    "c#", "dotnet", "net gen", "infra", "cicd", "git", "api", "rest", "graphql", "tailwind",
    "css", "html", "php", "laravel", "symfony", "ruby", "rails", "go", "golang", "rust"
  ];
  const domainKeywords = [
    "agile", "scrum", "devops", "management", "analyst", "analist", "consultant", "architect", 
    "veiligheid", "security", "infra", "governance", "compliance", "prince2", "itil", 
    "product owner", "scrum master", "coaching", "systeem", "netwerk", "ontwikkeling"
  ];
  
  if (techKeywords.some(kw => name.includes(kw))) return "tech";
  if (domainKeywords.some(kw => name.includes(kw))) return "domain";
  return "other";
}

export default function ConsultantProfile() {
  const t = useT();
  const repoPath = useAppStore((s) => s.repoPath);
  const user = useAppStore((s) => s.user);
  const lensUser = useAppStore((s) => s.lensUser);
  const activeCandidateFolder = useAppStore((s) => s.activeCandidateFolder);
  const hasMasterCv = useAppStore((s) => s.hasMasterCv);
  const setHasMasterCv = useAppStore((s) => s.setHasMasterCv);
  const [, navigate] = useLocation();
  const language = useAppStore((s) => s.language);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"details" | "documents">("details");
  const [skillsTab, setSkillsTab] = useState<"confirmed" | "inferred" | "gaps">("confirmed");
  const [skillsSubTab, setSkillsSubTab] = useState<"tech" | "domain" | "other">("tech");

  const accessToken = useAppStore((s) => s.accessToken);
  const settings = useAppStore((s) => s.settings);

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [autoExtracting, setAutoExtracting] = useState(false);
  const [autoExtractError, setAutoExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editExtracting, setEditExtracting] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string; email: string; phone: string; location: string;
    nationality: string; linkedin: string; language_dutch: string;
    language_english: string; seniority: string; sector_experience: string;
    available_from: string; employer: string;
  }>({
    name: "", email: "", phone: "", location: "", nationality: "",
    linkedin: "", language_dutch: "", language_english: "", seniority: "",
    sector_experience: "", available_from: "", employer: "",
  });

  function profileToForm(p: Profile) {
    return {
      name: p.personal?.name ?? "",
      email: p.personal?.email ?? "",
      phone: p.personal?.phone ?? "",
      location: p.personal?.location ?? "",
      nationality: p.personal?.nationality ?? "",
      linkedin: p.personal?.linkedin ?? "",
      language_dutch: p.personal?.language_dutch ?? "",
      language_english: p.personal?.language_english ?? "",
      seniority: p.seniority ?? "",
      sector_experience: (p.sector_experience ?? []).join(", "),
      available_from: p.personal?.available_from ?? "",
      employer: p.personal?.employer ?? "",
    };
  }

  async function runAutoExtract() {
    if (!repoPath || !activeCandidateFolder || !settings?.anthropic_api_key) return;
    setAutoExtracting(true);
    setAutoExtractError(null);
    try {
      const cvText = await readBestCvText(repoPath, activeCandidateFolder);
      if (!cvText || cvText.trim().length === 0) {
        throw new Error(
          language === "nl"
            ? "Geen Master CV gevonden — upload eerst het Master CV."
            : "No Master CV found — please upload the Master CV first."
        );
      }
      const graphMe = accessToken ? await fetchGraphMe(accessToken) : null;

      const extracted = await extractProfileFromCV(
        settings.anthropic_api_key,
        settings.anthropic_model ?? "gemini-2.5-flash",
        cvText,
      );

      const base = profile ?? {
        personal: { name: "", email: "" },
        confirmed_skills: [], inferred_skills: [], confirmed_gaps: [],
      };
      const updated: Profile = {
        ...base,
        personal: {
          ...base.personal,
          name:             extracted.name || graphMe?.displayName || base.personal.name || "",
          email:            extracted.email || graphMe?.mail || graphMe?.userPrincipalName || base.personal.email || "",
          phone:            extracted.phone || graphMe?.mobilePhone || base.personal.phone || undefined,
          location:         extracted.location || graphMe?.officeLocation || base.personal.location || undefined,
          nationality:      extracted.nationality || base.personal.nationality || undefined,
          linkedin:         extracted.linkedin || base.personal.linkedin || undefined,
          language_dutch:   extracted.language_dutch || base.personal.language_dutch || undefined,
          language_english: extracted.language_english || base.personal.language_english || undefined,
          available_from:   extracted.available_from || base.personal.available_from || undefined,
          employer:         extracted.employer || graphMe?.jobTitle || base.personal.employer || undefined,
        },
        seniority: extracted.seniority || base.seniority || undefined,
        sector_experience: extracted.sector_experience.length > 0
          ? extracted.sector_experience
          : base.sector_experience,
        confirmed_skills: extracted.confirmed_skills && extracted.confirmed_skills.length > 0
          ? extracted.confirmed_skills.map(s => ({
              skill: s.skill,
              years: s.years,
              evidence: s.evidence,
              source: "extraction",
              confirmed_at: new Date().toISOString()
            }))
          : base.confirmed_skills,
        inferred_skills: extracted.inferred_skills && extracted.inferred_skills.length > 0
          ? extracted.inferred_skills.map(s => ({
              skill: s.skill,
              confidence: s.confidence,
              inference_basis: s.inference_basis,
              confirmed: false,
              confirmed_at: null
            }))
          : base.inferred_skills,
      };
      await writeProfileJson(repoPath, activeCandidateFolder, updated);
      setProfile(updated);
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      console.error("Auto-extract failed", msg);
      setAutoExtractError(msg);
    } finally {
      setAutoExtracting(false);
    }
  }

  async function openEditModal(autoExtract = false) {
    const base = profile ? profileToForm(profile) : { ...editForm };
    setEditForm(base);
    setEditError(null);
    setEditOpen(true);

    if (!autoExtract || !repoPath || !activeCandidateFolder || !settings?.anthropic_api_key) return;

    setEditExtracting(true);
    try {
      // Parallel: AI extraction from CV + M365 /me
      const [cvText, graphMe] = await Promise.all([
        readBestCvText(repoPath, activeCandidateFolder),
        accessToken ? fetchGraphMe(accessToken) : Promise.resolve(null),
      ]);

      const extracted = await extractProfileFromCV(
        settings.anthropic_api_key,
        settings.anthropic_model ?? "gemini-2.5-flash",
        cvText,
      );

      // Merge: CV extraction is primary; Entra enriches only fields the CV left blank; existing fills remaining gaps.
      setEditForm((prev) => ({
        name:             extracted.name || graphMe?.displayName || prev.name,
        email:            extracted.email || graphMe?.mail || graphMe?.userPrincipalName || prev.email,
        phone:            extracted.phone || graphMe?.mobilePhone || prev.phone,
        location:         extracted.location || graphMe?.officeLocation || prev.location,
        nationality:      extracted.nationality || prev.nationality,
        linkedin:         extracted.linkedin || prev.linkedin,
        language_dutch:   extracted.language_dutch || prev.language_dutch,
        language_english: extracted.language_english || prev.language_english,
        seniority:        extracted.seniority || prev.seniority,
        sector_experience: extracted.sector_experience.length > 0
          ? extracted.sector_experience.join(", ")
          : prev.sector_experience,
        available_from:   extracted.available_from || prev.available_from,
        employer:         extracted.employer || graphMe?.jobTitle || prev.employer,
      }));

      // Save extracted skills and inferences to profile.json directly
      const baseProf = profile ?? {
        personal: { name: "", email: "" },
        confirmed_skills: [], inferred_skills: [], confirmed_gaps: [],
      };
      const updatedProf: Profile = {
        ...baseProf,
        confirmed_skills: extracted.confirmed_skills && extracted.confirmed_skills.length > 0
          ? extracted.confirmed_skills.map(s => ({
              skill: s.skill,
              years: s.years,
              evidence: s.evidence,
              source: "extraction",
              confirmed_at: new Date().toISOString()
            }))
          : baseProf.confirmed_skills,
        inferred_skills: extracted.inferred_skills && extracted.inferred_skills.length > 0
          ? extracted.inferred_skills.map(s => ({
              skill: s.skill,
              confidence: s.confidence,
              inference_basis: s.inference_basis,
              confirmed: false,
              confirmed_at: null
            }))
          : baseProf.inferred_skills,
      };
      await writeProfileJson(repoPath, activeCandidateFolder, updatedProf);
      setProfile(updatedProf);
    } catch (err: any) {
      setEditError((language === "nl" ? "AI-extractie mislukt: " : "AI extraction failed: ") + (err?.message ?? ""));
    } finally {
      setEditExtracting(false);
    }
  }

  async function handleSaveProfile() {
    if (!repoPath || !activeCandidateFolder) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const current = profile ?? {
        personal: { name: "", email: "" },
        confirmed_skills: [],
        inferred_skills: [],
        confirmed_gaps: [],
      };
      const updated: Profile = {
        ...current,
        personal: {
          ...current.personal,
          name: editForm.name,
          email: editForm.email,
          phone: editForm.phone || undefined,
          location: editForm.location || undefined,
          nationality: editForm.nationality || undefined,
          linkedin: editForm.linkedin || undefined,
          language_dutch: editForm.language_dutch || undefined,
          language_english: editForm.language_english || undefined,
          available_from: editForm.available_from || undefined,
          employer: editForm.employer || undefined,
        },
        seniority: editForm.seniority || undefined,
        sector_experience: editForm.sector_experience
          ? editForm.sector_experience.split(",").map((s) => s.trim()).filter(Boolean)
          : current.sector_experience,
      };
      await writeProfileJson(repoPath, activeCandidateFolder, updated);
      setProfile(updated);
      setEditOpen(false);
    } catch (err: any) {
      setEditError(err?.message ?? "Save failed.");
    } finally {
      setEditSaving(false);
    }
  }

  const displayName = profile?.personal?.name ?? lensUser?.display_name ?? user?.displayName ?? "Consultant";
  const email = profile?.personal?.email ?? lensUser?.email ?? user?.email ?? "";

  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    if (!repoPath || !activeCandidateFolder) return;

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [prof, allWorkflows, allVacancies] = await Promise.all([
          readProfileJson(repoPath, activeCandidateFolder),
          readAllWorkflows(repoPath, activeCandidateFolder),
          readAllVacancies(repoPath),
        ]);
        if (active) {
          setProfile(prof);
          setWorkflows(allWorkflows);
          setVacancies(allVacancies);
        }
      } catch (err) {
        console.error("Error loading profile details", err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [repoPath, activeCandidateFolder, reloadNonce]);

  async function handleTerminate(vacancyId: string) {
    if (!repoPath || !activeCandidateFolder) return;
    const confirmed = window.confirm(
      language === "nl"
        ? "Weet u zeker dat u deze workflow permanent wilt beëindigen?"
        : "Are you sure you want to permanently terminate this workflow?"
    );
    if (!confirmed) return;
    try {
      await terminateWorkflow(repoPath, activeCandidateFolder, vacancyId);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      console.error("Failed to terminate workflow", err);
    }
  }

  // Upload handlers
  async function handleFile(file: File) {
    if (!file) return;

    if (!file.name.endsWith(".docx")) {
      setUploadError(t("firstrun.invalid_file"));
      return;
    }

    setUploadLoading(true);
    setUploadError(null);

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (!arrayBuffer) {
          throw new Error("Could not read file data");
        }

        try {
          // Extract text using mammoth in the webview
          const { extractTextFromDocx } = await import("../../lib/documents/cv-extractor");
          const extractedText = await extractTextFromDocx(arrayBuffer);
          
          if (!repoPath || !activeCandidateFolder) {
            throw new Error("Repository path or active candidate folder is missing");
          }

          // Save both human_master_cv.docx and human_master_cv.txt
          await saveMasterCv(repoPath, activeCandidateFolder, arrayBuffer, extractedText);

          setUploadSuccess(true);
          setTimeout(() => {
            setHasMasterCv(true);
            setUploadSuccess(false);
            setUploadLoading(false);

            // Auto-extract profile in the background — no modal required
            if (settings?.anthropic_api_key) {
              void runAutoExtract();
            } else {
              readProfileJson(repoPath, activeCandidateFolder).then(setProfile).catch(console.error);
            }
          }, 1500);
        } catch (err) {
          console.error(err);
          setUploadError(t("common.error"));
          setUploadLoading(false);
        }
      };

      reader.onerror = () => {
        setUploadError(t("common.error"));
        setUploadLoading(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error(err);
      setUploadError(t("common.error"));
      setUploadLoading(false);
    }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (uploadLoading || uploadSuccess) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }

  function triggerFileSelect() {
    if (uploadLoading || uploadSuccess) return;
    fileInputRef.current?.click();
  }

  // Handle M365 deep links natively via Tauri Opener
  function handleEmailClick(e: React.MouseEvent) {
    e.preventDefault();
    if (email) {
      openExternal(`https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(email)}`);
    }
  }

  function handleTeamsClick(e: React.MouseEvent) {
    e.preventDefault();
    if (email) {
      openExternal(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(email)}`);
    }
  }

  function handlePhoneClick(e: React.MouseEvent, phone?: string) {
    e.preventDefault();
    if (phone) {
      openExternal(`tel:${phone}`);
    }
  }

  function handleLinkedInClick(e: React.MouseEvent, linkedin?: string) {
    e.preventDefault();
    if (linkedin) {
      // make sure it starts with http/https
      const url = linkedin.startsWith("http") ? linkedin : `https://${linkedin}`;
      openExternal(url);
    }
  }

  // v1.7 opened generated .docx files in Word via Tauri's openPath. In v2.0
  // documents live in Blob Storage (integration pending). For now, notify.
  async function handleOpenDocument(filename: string) {
    console.warn(`Document open pending Blob Storage integration: ${filename}`);
    alert("Document download is not yet available in the web version — coming in a later release.");
  }

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-slate-800" />
          <p className="text-sm font-medium text-slate-500">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Fallback defaults for personal info if profile.json does not exist yet
  const personal = profile?.personal ?? {
    name: displayName,
    email: email,
    phone: "+31 6 12345678",
    location: "Netherlands",
    nationality: "Dutch",
    linkedin: "linkedin.com",
    language_dutch: "Native",
    language_english: "Fluent",
    employment_status: "Employee",
    employer: "Adapt Interim Professionals",
    available_from: "Immediately",
  };

  const confirmedSkills = profile?.confirmed_skills ?? [];
  const inferredSkills = profile?.inferred_skills ?? [];
  const confirmedGaps = profile?.confirmed_gaps ?? [];

  return (
    <>
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      
      {/* Page Header */}
      <div className="border-b border-slate-200/60 pb-5">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          {t("nav.profile")}
        </h1>
      </div>

      {/* Auto-extraction banner */}
      {(autoExtracting || autoExtractError) && (
        <div className={`mt-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${
          autoExtractError
            ? "border-rose-200 bg-rose-50/70"
            : "border-indigo-200 bg-indigo-50/70"
        }`}>
          {autoExtracting
            ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600 shrink-0 mt-0.5" />
            : <span className="text-rose-500 shrink-0">⚠</span>}
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${autoExtractError ? "text-rose-700" : "text-indigo-700"}`}>
              {autoExtracting
                ? (language === "nl"
                    ? "AI extraheert profielgegevens uit CV — een moment…"
                    : "AI is extracting profile details from CV — just a moment…")
                : (language === "nl" ? "Extractie mislukt" : "Extraction failed")}
            </p>
            {autoExtractError && (
              <p className="text-xs text-rose-600 mt-0.5 break-words">{autoExtractError}</p>
            )}
          </div>
          {autoExtractError && (
            <button
              onClick={() => setAutoExtractError(null)}
              className="text-rose-400 hover:text-rose-600 text-sm leading-none shrink-0 cursor-pointer"
            >✕</button>
          )}
        </div>
      )}

      {/* Profile Layout Grid */}
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        
        {/* Left Col: Contact Details Card (with tabs) */}
        <div className="space-y-6 lg:col-span-1">
          
          {/* Contact Details Card */}
          <section className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
            <div className="flex items-start justify-between mb-2">
              {hasMasterCv && settings?.anthropic_api_key ? (
                <button
                  type="button"
                  disabled={autoExtracting}
                  onClick={(e) => { e.preventDefault(); void runAutoExtract(); }}
                  className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-indigo-600 cursor-pointer transition-colors disabled:cursor-wait disabled:opacity-50"
                  title={language === "nl" ? "Profielgegevens opnieuw extraheren uit CV" : "Re-extract profile details from CV"}
                >
                  {autoExtracting
                    ? <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-500" />
                    : <span>↺</span>}
                  {language === "nl" ? "Uit CV" : "From CV"}
                </button>
              ) : <span />}
              <button
                type="button"
                onClick={() => void openEditModal(false)}
                className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 cursor-pointer transition-colors"
              >
                {language === "nl" ? "Bewerken" : "Edit Details"}
              </button>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-linear-to-br from-slate-700 to-slate-900 text-white text-xl font-bold shadow-md">
                {displayName.substring(0,2).toUpperCase()}
              </div>
              <h2 className="mt-4 text-lg font-bold text-slate-800">{personal.name}</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">{personal.employer}</p>
            </div>

            {/* Tab Bar */}
            <div className="mt-5 flex border-b border-slate-100">
              <button
                type="button"
                onClick={() => setActiveTab("details")}
                className={`flex-1 pb-2 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-all cursor-pointer ${
                  activeTab === "details"
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {language === "nl" ? "Gegevens" : "Details"}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("documents")}
                className={`flex-1 pb-2 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-all cursor-pointer ${
                  activeTab === "documents"
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {language === "nl" ? "Documenten" : "Documents"}
              </button>
            </div>

            {activeTab === "details" ? (
              <>
                {/* M365 Deep Links Grid */}
                <div className="mt-6 grid grid-cols-4 gap-2 border-t border-slate-50 pt-5">
                  <button
                    onClick={handleEmailClick}
                    className="flex flex-col items-center gap-1.5 rounded-lg py-2 hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none"
                    title="Send Outlook Email"
                  >
                    <span className="text-lg">📧</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Mail</span>
                  </button>

                  <button
                    onClick={handleTeamsClick}
                    className="flex flex-col items-center gap-1.5 rounded-lg py-2 hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none"
                    title="Chat on MS Teams"
                  >
                    <span className="text-lg">💬</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Teams</span>
                  </button>

                  <button
                    onClick={(e) => handlePhoneClick(e, personal.phone)}
                    disabled={!personal.phone}
                    className="flex flex-col items-center gap-1.5 rounded-lg py-2 hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Call Consultant"
                  >
                    <span className="text-lg">📞</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Phone</span>
                  </button>

                  <button
                    onClick={(e) => handleLinkedInClick(e, personal.linkedin)}
                    disabled={!personal.linkedin}
                    className="flex flex-col items-center gap-1.5 rounded-lg py-2 hover:bg-slate-50 transition-colors cursor-pointer focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                    title="View LinkedIn Profile"
                  >
                    <span className="text-lg">🔗</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">LinkedIn</span>
                  </button>
                </div>

                {/* Core Info List */}
                <div className="mt-6 space-y-3 border-t border-slate-50 pt-5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Availability:</span>
                    <span className="font-semibold text-slate-700">{personal.available_from ?? "Immediately"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Location:</span>
                    <span className="font-semibold text-slate-700">{personal.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">Dutch Level:</span>
                    <span className="font-semibold text-slate-700">{personal.language_dutch ?? "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 font-medium">English Level:</span>
                    <span className="font-semibold text-slate-700">{personal.language_english ?? "N/A"}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="mt-5 space-y-4">
                
                {/* Human Master CV Card */}
                {hasMasterCv ? (
                  <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/40 p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">📄</span>
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700">{t("profile.master_cv")}</span>
                        <span className="text-5xs text-slate-400 font-semibold uppercase tracking-wider mt-0.5">Word .docx</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenDocument("human_master_cv.docx")}
                      className="rounded-md bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-600 transition-colors cursor-pointer focus:outline-none"
                    >
                      Open
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                      onClick={triggerFileSelect}
                      className={`group flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-4 text-center transition-all duration-200 cursor-pointer ${
                        isDragging
                          ? "border-indigo-600 bg-indigo-50/50 scale-[0.99]"
                          : "border-slate-300 bg-slate-50/55 hover:border-indigo-400 hover:bg-white"
                      } ${uploadLoading || uploadSuccess ? "opacity-60 pointer-events-none" : ""}`}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={onFileChange}
                        accept=".docx"
                        className="hidden"
                        disabled={uploadLoading || uploadSuccess}
                      />
                      {uploadLoading ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                      ) : uploadSuccess ? (
                        <span className="text-emerald-600 text-xs font-semibold">✓ Uploaded</span>
                      ) : (
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-semibold text-indigo-600">Upload Master CV</span>
                          <span className="text-5xs text-slate-400 mt-0.5 uppercase tracking-wider font-bold">Word .docx only</span>
                        </div>
                      )}
                    </div>
                    {uploadError && (
                      <p className="text-[10px] font-semibold text-red-600 text-center">{uploadError}</p>
                    )}
                  </div>
                )}

                {/* System Master CV Card */}
                <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50/40 p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🤖</span>
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{t("profile.system_cv")}</span>
                      <span className="text-5xs text-slate-400 font-semibold uppercase tracking-wider mt-0.5">
                        {profile?.system_master_cv_last_consolidated 
                          ? `Consolidated ${new Date(profile.system_master_cv_last_consolidated).toLocaleDateString()}`
                          : "Not consolidated yet"}
                      </span>
                    </div>
                  </div>
                  {profile?.system_master_cv_last_consolidated ? (
                    <button
                      onClick={() => handleOpenDocument("system_master_cv.docx")}
                      className="rounded-md bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-600 transition-colors cursor-pointer"
                    >
                      Open
                    </button>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mr-2">🔒 Locked</span>
                  )}
                </div>

                {/* Consolidate Profile Button */}
                <div className="mt-5 border-t border-slate-50 pt-4 space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate("/consolidation")}
                    className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-800 px-4 py-2 text-xs font-semibold text-white shadow-xs transition-all cursor-pointer"
                  >
                    {t("profile.consolidate")}
                  </button>
                  {profile?.system_master_cv_last_consolidated && (
                    <p className="text-[9px] text-center text-slate-400">
                      {profile.consolidation_source_count ?? 0}{" "}
                      {language === "nl" ? "bronnen verwerkt" : "sources processed"}
                    </p>
                  )}
                </div>

              </div>
            )}
          </section>

        </div>

        {/* Right Col: Skills Profile (2 cols) */}
        <div className="space-y-6 lg:col-span-2">
          
          {/* Profile Enrichment & Skills Section */}
          <section className="rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">

            {/* Section header + summary counts */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h3 className="text-base font-bold text-slate-800 tracking-tight">
                  Skills Profile
                </h3>
                {profile?.last_enriched_at && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Last enriched {new Date(profile.last_enriched_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200/60 px-2.5 py-1 text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />
                  {confirmedSkills.length} confirmed
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200/60 px-2.5 py-1 text-[10px] font-bold text-amber-700 uppercase tracking-wider">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 inline-block" />
                  {inferredSkills.length} inferred
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200/60 px-2.5 py-1 text-[10px] font-bold text-rose-700 uppercase tracking-wider">
                  <span className="h-1.5 w-1.5 rounded-full bg-rose-400 inline-block" />
                  {confirmedGaps.length} gaps
                </span>
              </div>
            </div>

            {/* Seniority + sector tags */}
            {(profile?.seniority || (profile?.sector_experience ?? []).length > 0) && (
              <div className="flex flex-wrap items-center gap-2 py-3.5 border-b border-slate-100/70">
                {profile?.seniority && (
                  <span className="rounded-full bg-slate-900 text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                    {profile.seniority}
                  </span>
                )}
                {(profile?.sector_experience ?? []).map((sector, i) => (
                  <span key={i} className="rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider">
                    {sector}
                  </span>
                ))}
              </div>
            )}

            {/* Tab selector for Skills */}
            <div className="mt-5 flex border-b border-slate-100">
              <button
                type="button"
                onClick={() => { setSkillsTab("confirmed"); setSkillsSubTab("tech"); }}
                className={`flex-1 pb-2 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-all cursor-pointer ${
                  skillsTab === "confirmed"
                    ? "border-emerald-500 text-emerald-700"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {t("profile.confirmed_skills")} ({confirmedSkills.length})
              </button>
              <button
                type="button"
                onClick={() => { setSkillsTab("inferred"); setSkillsSubTab("tech"); }}
                className={`flex-1 pb-2 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-all cursor-pointer ${
                  skillsTab === "inferred"
                    ? "border-amber-400 text-amber-700"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {t("profile.inferred_skills")} ({inferredSkills.length})
              </button>
              <button
                type="button"
                onClick={() => { setSkillsTab("gaps"); setSkillsSubTab("tech"); }}
                className={`flex-1 pb-2 text-xs font-bold uppercase tracking-wider text-center border-b-2 transition-all cursor-pointer ${
                  skillsTab === "gaps"
                    ? "border-rose-400 text-rose-700"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                {t("profile.confirmed_gaps")} ({confirmedGaps.length})
              </button>
            </div>

            {/* Sub-tab selector for categories */}
            {profile && (
              <div className="mt-4 flex gap-1.5 p-1 bg-slate-50 rounded-lg">
                <button
                  type="button"
                  onClick={() => setSkillsSubTab("tech")}
                  className={`flex-1 py-1.5 text-[10px] font-bold uppercase tracking-wider text-center rounded-md transition-all cursor-pointer ${
                    skillsSubTab === "tech"
                      ? "bg-white text-slate-800 shadow-xs border border-slate-100/40"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {language === "nl" ? "Talen & Tech" : "Tech"}
                </button>
                <button
                  type="button"
                  onClick={() => setSkillsSubTab("domain")}
                  className={`flex-1 py-1.5 text-[10px] font-bold uppercase tracking-wider text-center rounded-md transition-all cursor-pointer ${
                    skillsSubTab === "domain"
                      ? "bg-white text-slate-800 shadow-xs border border-slate-100/40"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {language === "nl" ? "Methodieken" : "Methodologies"}
                </button>
                <button
                  type="button"
                  onClick={() => setSkillsSubTab("other")}
                  className={`flex-1 py-1.5 text-[10px] font-bold uppercase tracking-wider text-center rounded-md transition-all cursor-pointer ${
                    skillsSubTab === "other"
                      ? "bg-white text-slate-800 shadow-xs border border-slate-100/40"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {language === "nl" ? "Overige" : "Other"}
                </button>
              </div>
            )}

            {!profile ? (
              <div className="py-12 text-center">
                <p className="text-sm text-slate-400">No enriched skills information yet.</p>
                <p className="text-xs text-slate-400 mt-1">Start vacancy matching workflows to build the skills inventory.</p>
              </div>
            ) : (
              <div className="mt-6">
                {/* ── TIER 1: Confirmed Skills ─────────────────────────── */}
                {skillsTab === "confirmed" && (
                  <div>
                    {confirmedSkills.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-4">
                        No confirmed skills yet — answer gap questions to build this list.
                      </p>
                    ) : (
                      <div className="space-y-6">
                        {[
                          {
                            category: "tech",
                            label: language === "nl" ? "Talen & Technieken" : "Languages & Technologies",
                            items: confirmedSkills.filter(s => categorizeSkill(s.skill) === "tech"),
                            borderClass: "border-l-emerald-400 border-emerald-100 bg-emerald-50/15 hover:bg-emerald-50/45",
                            badgeClass: "bg-emerald-100 text-emerald-700"
                          },
                          {
                            category: "domain",
                            label: language === "nl" ? "Methodieken & Processen" : "Methodologies & Processes",
                            items: confirmedSkills.filter(s => categorizeSkill(s.skill) === "domain"),
                            borderClass: "border-l-indigo-400 border-indigo-100 bg-indigo-50/15 hover:bg-indigo-50/45",
                            badgeClass: "bg-indigo-100 text-indigo-700"
                          },
                          {
                            category: "other",
                            label: language === "nl" ? "Overige Competenties" : "Other Competencies",
                            items: confirmedSkills.filter(s => categorizeSkill(s.skill) === "other"),
                            borderClass: "border-l-slate-400 border-slate-200 bg-slate-50/20 hover:bg-slate-100/40",
                            badgeClass: "bg-slate-200 text-slate-700"
                          }
                        ]
                          .filter(({ category }) => skillsSubTab === category)
                          .map(({ label, items, borderClass, badgeClass }) => {
                            if (items.length === 0) return null;
                            return (
                              <div key={label}>
                                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 border-b border-slate-100 pb-1.5">{label}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {items.map((s, idx) => (
                                    <div key={idx} className="relative group">
                                      <div className={`rounded-lg border px-3 py-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700 transition-all cursor-default border-l-3 ${borderClass}`}>
                                        <span>{s.skill}</span>
                                        <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-extrabold ${badgeClass}`}>
                                          {s.years}yr
                                        </span>
                                      </div>
                                      <div className="absolute left-1/2 bottom-full mb-2 z-30 hidden group-hover:block -translate-x-1/2 w-64 bg-slate-900 text-white text-[10px] p-2.5 rounded-lg shadow-xl leading-relaxed whitespace-normal pointer-events-none transition-all">
                                        <p className="font-bold border-b border-slate-700/60 pb-1 mb-1">{s.skill} ({s.years} {s.years === 1 ? "year" : "years"})</p>
                                        <p className="italic text-slate-300">{s.evidence}</p>
                                        {s.source && (
                                          <p className="mt-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                                            via {s.source} {s.confirmed_at && `· ${new Date(s.confirmed_at).toLocaleDateString()}`}
                                          </p>
                                        )}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── TIER 2: Inferred Skills ──────────────────────────── */}
                {skillsTab === "inferred" && (
                  <div>
                    {inferredSkills.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-4">No inferred skills yet.</p>
                    ) : (
                      <div className="space-y-6">
                        {[
                          {
                            category: "tech",
                            label: language === "nl" ? "Talen & Technieken" : "Languages & Technologies",
                            items: inferredSkills.filter(s => categorizeSkill(s.skill) === "tech"),
                            borderClass: "border-l-amber-400 border-amber-100 bg-amber-50/10 hover:bg-amber-50/30"
                          },
                          {
                            category: "domain",
                            label: language === "nl" ? "Methodieken & Processen" : "Methodologies & Processes",
                            items: inferredSkills.filter(s => categorizeSkill(s.skill) === "domain"),
                            borderClass: "border-l-indigo-400 border-indigo-100 bg-indigo-50/15 hover:bg-indigo-50/45"
                          },
                          {
                            category: "other",
                            label: language === "nl" ? "Overige Competenties" : "Other Competencies",
                            items: inferredSkills.filter(s => categorizeSkill(s.skill) === "other"),
                            borderClass: "border-l-slate-400 border-slate-200 bg-slate-50/20 hover:bg-slate-100/40"
                          }
                        ]
                          .filter(({ category }) => skillsSubTab === category)
                          .map(({ label, items, borderClass }) => {
                            if (items.length === 0) return null;
                            return (
                              <div key={label}>
                                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 border-b border-slate-100 pb-1.5">{label}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {items.map((s, idx) => (
                                    <div key={idx} className="relative group">
                                      <div className={`rounded-lg border px-3 py-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700 transition-all cursor-default border-l-3 ${borderClass}`}>
                                        <span>{s.skill}</span>
                                        <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-extrabold uppercase ${
                                          s.confidence === "high"
                                            ? "bg-emerald-100 text-emerald-700"
                                            : s.confidence === "medium"
                                            ? "bg-amber-100 text-amber-700"
                                            : "bg-slate-100 text-slate-600"
                                        }`}>
                                          {s.confidence}
                                        </span>
                                      </div>
                                      <div className="absolute left-1/2 bottom-full mb-2 z-30 hidden group-hover:block -translate-x-1/2 w-64 bg-slate-900 text-white text-[10px] p-2.5 rounded-lg shadow-xl leading-relaxed whitespace-normal pointer-events-none transition-all">
                                        <p className="font-bold border-b border-slate-700/60 pb-1 mb-1">{s.skill} ({s.confidence} confidence)</p>
                                        <p className="italic text-slate-300">{s.inference_basis}</p>
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── TIER 3: Gaps ─────────────────────────────────────── */}
                {skillsTab === "gaps" && (
                  <div>
                    {confirmedGaps.length === 0 ? (
                      <p className="text-xs text-slate-400 italic py-4">No confirmed gaps on record.</p>
                    ) : (
                      <div className="space-y-6">
                        {[
                          {
                            category: "tech",
                            label: language === "nl" ? "Talen & Technieken" : "Languages & Technologies",
                            items: confirmedGaps.filter(s => categorizeSkill(s.skill) === "tech"),
                            borderClass: "border-l-rose-400 border-rose-100 bg-rose-50/15 hover:bg-rose-50/45"
                          },
                          {
                            category: "domain",
                            label: language === "nl" ? "Methodieken & Processen" : "Methodologies & Processes",
                            items: confirmedGaps.filter(s => categorizeSkill(s.skill) === "domain"),
                            borderClass: "border-l-indigo-400 border-indigo-100 bg-indigo-50/15 hover:bg-indigo-50/45"
                          },
                          {
                            category: "other",
                            label: language === "nl" ? "Overige Competenties" : "Other Competencies",
                            items: confirmedGaps.filter(s => categorizeSkill(s.skill) === "other"),
                            borderClass: "border-l-slate-400 border-slate-200 bg-slate-50/20 hover:bg-slate-100/40"
                          }
                        ]
                          .filter(({ category }) => skillsSubTab === category)
                          .map(({ label, items, borderClass }) => {
                            if (items.length === 0) return null;
                            return (
                              <div key={label}>
                                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5 border-b border-slate-100 pb-1.5">{label}</h4>
                                <div className="flex flex-wrap gap-2">
                                  {items.map((s, idx) => (
                                    <div key={idx} className="relative group">
                                      <div className={`rounded-lg border px-3 py-1.5 flex items-center gap-2 text-xs font-semibold text-slate-700 transition-all cursor-default border-l-3 ${borderClass}`}>
                                        <span>{s.skill}</span>
                                        <span className="shrink-0 rounded bg-rose-100 px-1 py-0.5 text-[9px] font-extrabold text-rose-700 uppercase">
                                          absent
                                        </span>
                                      </div>
                                      <div className="absolute left-1/2 bottom-full mb-2 z-30 hidden group-hover:block -translate-x-1/2 w-64 bg-slate-900 text-white text-[10px] p-2.5 rounded-lg shadow-xl leading-relaxed whitespace-normal pointer-events-none transition-all">
                                        <p className="font-bold border-b border-slate-700/60 pb-1 mb-1">{s.skill} (Absent)</p>
                                        <p className="italic text-slate-300">{s.notes}</p>
                                        {s.confirmed_at && (
                                          <p className="mt-1.5 text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                                            Confirmed: {new Date(s.confirmed_at).toLocaleDateString()}
                                          </p>
                                        )}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

        </div>

      </div>

      {/* Workflows History List (Full Width Datagrid) */}
      <section className="mt-8 rounded-xl border border-slate-200/60 bg-white p-6 shadow-xs">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
          <h3 className="text-base font-bold text-slate-800 tracking-tight">
            {language === "nl" ? "Workflow Historie" : "Workflow History"}
          </h3>
          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
            {workflows.length} {language === "nl" ? "actief" : "active"}
          </span>
        </div>
        
        {workflows.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-xs text-slate-400">No matching workflows have been created yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-600 border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="py-3 px-4">{language === "nl" ? "Opdrachtgever" : "Client"}</th>
                  <th className="py-3 px-4">{language === "nl" ? "Functie" : "Vacancy"}</th>
                  <th className="py-3 px-4">{language === "nl" ? "Gestart op" : "Started"}</th>
                  <th className="py-3 px-4">{language === "nl" ? "Status" : "Status"}</th>
                  <th className="py-3 px-4 text-right">{language === "nl" ? "Acties" : "Actions"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {workflows.map((wf) => {
                  const vacancy = vacancies.find((v) => v.id === wf.vacancy_id);
                  return (
                    <tr key={wf.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-slate-800">
                        {vacancy?.client ?? "Unknown Client"}
                      </td>
                      <td className="py-3.5 px-4 text-slate-700">
                        {vacancy?.title ?? "Unknown Role"}
                      </td>
                      <td className="py-3.5 px-4 text-slate-400">
                        {new Date(wf.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          wf.status === "submitted" || wf.status === "placed"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : wf.status === "rejected"
                            ? "bg-red-50 text-red-700 border border-red-100"
                            : "bg-slate-100 text-slate-700 border border-slate-200/50"
                        }`}>
                          {t(`workflow.status.${wf.status}` as any)}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/workflows/${wf.id}`);
                            }}
                            className="rounded-md border border-indigo-200 bg-indigo-50/50 px-2.5 py-1 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 transition-colors cursor-pointer"
                            title={language === "nl" ? "Openen" : "Open"}
                          >
                            {language === "nl" ? "Openen" : "Open"}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handleTerminate(wf.vacancy_id);
                            }}
                            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors cursor-pointer"
                            title={language === "nl" ? "Beëindigen" : "Terminate"}
                          >
                            {language === "nl" ? "Beëindigen" : "Terminate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>

    {/* ── Profile Edit Modal ─────────────────────────────────────── */}
    {editOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
            <div>
              <h3 className="text-base font-bold text-slate-900">
                {language === "nl" ? "Persoonlijke gegevens" : "Profile Details"}
              </h3>
              {editExtracting && (
                <p className="text-[10px] text-indigo-500 font-medium mt-0.5 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500" />
                  {language === "nl" ? "AI extraheert gegevens uit CV…" : "AI extracting details from CV…"}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              className="text-slate-400 hover:text-slate-600 cursor-pointer text-lg leading-none"
            >
              ✕
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto px-6 py-5 space-y-4 flex-1">
            {editError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {editError}
              </div>
            )}

            {/* Row helper */}
            {[
              { key: "name",             label: language === "nl" ? "Naam" : "Name",               type: "text" },
              { key: "email",            label: "Email",                                            type: "email" },
              { key: "phone",            label: language === "nl" ? "Telefoon" : "Phone",           type: "tel" },
              { key: "location",         label: language === "nl" ? "Locatie" : "Location",         type: "text" },
              { key: "nationality",      label: language === "nl" ? "Nationaliteit" : "Nationality",type: "text" },
              { key: "linkedin",         label: "LinkedIn",                                         type: "text" },
              { key: "employer",         label: language === "nl" ? "Werkgever" : "Employer",       type: "text" },
              { key: "available_from",   label: language === "nl" ? "Beschikbaar per" : "Available from", type: "text" },
            ].map(({ key, label, type }) => (
              <div key={key}>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  {label}
                </label>
                <input
                  type={type}
                  value={(editForm as any)[key]}
                  onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))}
                  disabled={editExtracting}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none transition-colors disabled:opacity-50"
                />
              </div>
            ))}

            {/* Language proficiency */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "language_dutch",   label: language === "nl" ? "Nederlands" : "Dutch level" },
                { key: "language_english", label: language === "nl" ? "Engels" : "English level" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                    {label}
                  </label>
                  <select
                    value={(editForm as any)[key]}
                    onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))}
                    disabled={editExtracting}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none transition-colors disabled:opacity-50"
                  >
                    <option value="">—</option>
                    {["None", "A1", "A2", "B1", "B2", "C1", "C2", "Native"].map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {/* Seniority */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                {language === "nl" ? "Senioriteit" : "Seniority"}
              </label>
              <select
                value={editForm.seniority}
                onChange={(e) => setEditForm((f) => ({ ...f, seniority: e.target.value }))}
                disabled={editExtracting}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none transition-colors disabled:opacity-50"
              >
                <option value="">—</option>
                {["Junior", "Medior", "Senior", "Lead", "Principal"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Sector experience */}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                {language === "nl" ? "Sector ervaring (komma-gescheiden)" : "Sector experience (comma-separated)"}
              </label>
              <input
                type="text"
                value={editForm.sector_experience}
                onChange={(e) => setEditForm((f) => ({ ...f, sector_experience: e.target.value }))}
                disabled={editExtracting}
                placeholder="Overheid, Finance, IT"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
            <button
              type="button"
              onClick={() => setEditOpen(false)}
              className="rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 px-4 py-2 text-sm font-semibold cursor-pointer transition-colors"
            >
              {language === "nl" ? "Annuleren" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={() => void handleSaveProfile()}
              disabled={editSaving || editExtracting}
              className="rounded-lg bg-indigo-700 hover:bg-indigo-800 text-white px-5 py-2 text-sm font-semibold shadow-xs cursor-pointer transition-all disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {editSaving && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-white" />}
              {language === "nl" ? "Opslaan" : "Save"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
